import { useChatDock } from '@/app/providers/ChatDockContext';
import { useIsCompactLayout } from '@/common/hooks';
import { conversationEvents } from '@/lib/conversation-events';

/**
 * Header "open chat" button — shown when a module is open but the desktop split
 * is NOT active and the viewport is not mobile. Lives in the same
 * `.app-header-actions` slot as HeaderChatLauncher and wears the same
 * chat-bubble glyph, so "open chat" is always in one place. Clicking it closes
 * the module (→ the chat fills the screen) via the existing `closeModulePane`
 * event.
 *
 * Self-gating and mutually exclusive with HeaderChatLauncher:
 * - `isSplitViewActive` (module open AND desktop ≥768px) → the split shows the
 *   chat and the dock launcher owns "open chat", so this button hides.
 * - `isMobile` (≤767px) → the mobile single-view `chat | module` toggle owns it.
 *
 * Under the strict two-tier model (split live at ALL desktop widths — PDR 008
 * D3 as amended, plan §11 Track B3) these gates cover every steady state, so
 * this button no longer renders in normal operation; it is kept as the safety
 * affordance for transient windows where a module is open while the split flag
 * hasn't settled yet.
 *
 * One-way by design (no reopen-module affordance here) — modules reopen from the
 * always-visible sidebar rail.
 */
export function HeaderOpenChatButton() {
  const { isModulePaneOpen, isSplitViewActive } = useChatDock();
  const isMobile = useIsCompactLayout();

  if (!isModulePaneOpen || isSplitViewActive || isMobile) return null;

  const openChat = () => {
    window.dispatchEvent(new CustomEvent(conversationEvents.closeModulePane));
  };

  return (
    <button
      type="button"
      className="app-header-activities header-open-chat-btn"
      title="Open chat"
      aria-label="Open chat"
      onClick={openChat}
    >
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M7.9 20A9 9 0 1 0 4 16.1L2 22Z" />
      </svg>
    </button>
  );
}
